import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PackageSearch, Check, Truck, Home } from "lucide-react";

const STAGES = [
  { key: "placed", label: "Placed", Icon: PackageSearch },
  { key: "confirmed", label: "Confirmed", Icon: Check },
  { key: "shipped", label: "Shipped", Icon: Truck },
  { key: "delivered", label: "Delivered", Icon: Home },
];

export default function TrackOrderPage() {
  const [params] = useSearchParams();
  const [id, setId] = useState(params.get("id") || "");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function track(e) {
    if (e) e.preventDefault();
    if (!id) return;
    setLoading(true); setError(""); setOrder(null);
    try {
      const { data } = await api.get(`/orders/track/${encodeURIComponent(id)}`);
      setOrder(data);
    } catch { setError("Order not found"); }
    finally { setLoading(false); }
  }

  useEffect(() => { if (params.get("id")) track(); /* eslint-disable-line */ }, []);

  const currentIdx = order ? Math.max(0, STAGES.findIndex((s) => s.key === order.status)) : -1;

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-12">
      <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Order Tracking</div>
      <h1 className="mt-2 font-heading font-medium text-4xl sm:text-5xl">Track your order</h1>
      <form onSubmit={track} className="mt-8 flex gap-2 max-w-md">
        <input data-testid="track-input" value={id} onChange={(e) => setId(e.target.value.toUpperCase())} placeholder="SSR260215..." className="flex-1 h-12 px-4 rounded-full bg-secondary text-sm outline-none focus:ring-2 focus:ring-electric font-mono" />
        <button data-testid="track-submit" disabled={loading} className="h-12 px-6 rounded-full bg-electric text-white text-sm font-medium hover:bg-electric-600 disabled:opacity-60 transition-colors">Track</button>
      </form>
      {error && <div className="mt-4 text-sm text-destructive">{error}</div>}
      {order && (
        <div className="mt-10 p-6 rounded-2xl border border-border">
          <div className="flex flex-wrap items-baseline justify-between gap-4">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Order</div>
              <div className="font-heading font-medium text-2xl mt-1">{order.order_number}</div>
            </div>
            <div className="text-sm text-muted-foreground">Total: <span className="text-foreground font-medium tabular-nums">₹{order.total.toLocaleString()}</span></div>
          </div>
          <div className="mt-8 grid grid-cols-4 gap-2">
            {STAGES.map((s, i) => {
              const done = i <= currentIdx;
              return (
                <div key={s.key} className="flex flex-col items-center gap-2 text-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border ${done ? "border-electric bg-electric text-white" : "border-border text-muted-foreground"}`}>
                    <s.Icon className="w-4 h-4" />
                  </div>
                  <div className={`text-xs ${done ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</div>
                </div>
              );
            })}
          </div>
          <div className="mt-8 space-y-3">
            {order.timeline?.map((t, i) => (
              <div key={i} className="flex gap-3 text-sm">
                <div className="w-1.5 h-1.5 rounded-full bg-electric mt-2" />
                <div>
                  <div>{t.text}</div>
                  <div className="text-xs text-muted-foreground">{new Date(t.at).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 border-t pt-6">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">Items</div>
            <div className="space-y-2">
              {order.items?.map((it, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <div className="w-12 h-12 rounded-lg bg-secondary overflow-hidden">
                    {it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 truncate">{it.name} × {it.quantity}</div>
                  <div className="tabular-nums">₹{(it.price * it.quantity).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
